import React, { useState, useEffect } from 'react';
import { SuggestionData, SolutionData, SolutionDetails } from '@/types/solution';
import { convertToCanonicalFormat, convertToSolutionsTableFormat, reconstructCanonicalWithMetadata } from '@/utils/canonicalStructure';
import ReactDiffViewer from 'react-diff-viewer-continued';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { format } from 'date-fns';
import { CheckCircle, XCircle, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCurrentTags } from '@/services/tagService';
import { useTagValidation } from '@/hooks/useTagValidation';
import PreviewButton from '@/components/PreviewButton';
import TagValidationDialog from '@/components/TagValidationDialog';

interface ReviewSuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestion: SuggestionData;
  onAccept: (notes?: string, modifiedSuggestion?: string) => void;
  onReject: (notes?: string) => void;
}

const ReviewSuggestionDialog = ({
  open,
  onOpenChange,
  suggestion,
  onAccept,
  onReject
}: ReviewSuggestionDialogProps) => {
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [originalJsonData, setOriginalJsonData] = useState<string>('{}');
  // Check if this is a new solution (no existing solution_id)
  const isNewSolution = !suggestion?.solution_id || suggestion?.status === 'new_solution';
  
  const [originalSolutionDetails, setOriginalSolutionDetails] = useState<SolutionDetails | null>(null);
  const [suggestedSolutionDetails, setSuggestedSolutionDetails] = useState<SolutionDetails | null>(null);
  const [fullSolutionData, setFullSolutionData] = useState<SolutionData | null>(null);
  const [newJsonData, setNewJsonData] = useState<string>('{}');
  const [editedJsonData, setEditedJsonData] = useState<string>('{}');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(isNewSolution ? 'suggested' : 'diff');
  const [isLoading, setIsLoading] = useState(true);
  
  // Tag validation states
  const [showTagValidation, setShowTagValidation] = useState(false);
  const [pendingAcceptanceData, setPendingAcceptanceData] = useState<any>(null);
  
  const { validateTags, isValidating } = useTagValidation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update active tab when suggestion changes - ensure new solutions don't try to show diff tab
  useEffect(() => {
    if (isNewSolution && activeTab === 'diff') {
      setActiveTab('suggested');
    }
  }, [isNewSolution, activeTab]);
  
  useEffect(() => {
    if (open && suggestion) {
      setIsLoading(true);
      setReviewerNotes('');
      setActiveTab('diff');
      setIsEditing(false);
      setErrorMessage(null);
      setFullSolutionData(null);
      
      console.log('Opening suggestion review dialog for suggestion ID:', suggestion.id);
      console.log('Is new solution:', isNewSolution, 'solution_id:', suggestion.solution_id);
      
      let parsedSuggestion;
      try {
        const rawParsedSuggestion = JSON.parse(suggestion.suggestion);
        parsedSuggestion = convertToCanonicalFormat(rawParsedSuggestion);
        
        // Sort tags alphabetically to ensure consistent diffs
        if (parsedSuggestion.metadata && Array.isArray(parsedSuggestion.metadata.tags)) {
          parsedSuggestion.metadata.tags.sort();
        }
        
        setSuggestedSolutionDetails(parsedSuggestion);
        const formattedSuggestion = JSON.stringify(parsedSuggestion, null, 2);
        setNewJsonData(formattedSuggestion);
        setEditedJsonData(formattedSuggestion);
      } catch (e) {
        console.error('Error parsing suggestion JSON:', e);
        setNewJsonData(suggestion.suggestion);
        setEditedJsonData(suggestion.suggestion);
        setSuggestedSolutionDetails(null);
      }
      
      if (!isNewSolution) {
        const fetchOriginalData = async () => {
          try {
            const { data: detailsData, error: detailsError } = await supabase
              .from('solutions')
              .select('details')
              .eq('id', suggestion.solution_id)
              .single();
              
            if (detailsError) {
              console.error('Supabase error fetching details:', detailsError);
              setErrorMessage(`Failed to fetch original data: ${detailsError.message}`);
              setIsLoading(false);
              throw detailsError;
            }
            
            // Fetch complete solution data
            const { data: solutionData, error: solutionError } = await supabase
              .from('solutions')
              .select('*')
              .eq('id', suggestion.solution_id)
              .single();
              
            if (solutionError) {
              console.error('Supabase error fetching solution:', solutionError);
              setErrorMessage(`Failed to fetch solution data: ${solutionError.message}`);
              setIsLoading(false);
              throw solutionError;
            }
            
            if (detailsData && detailsData.details) {
              // Use the centralized function to reconstruct canonical format with metadata
              const originalCanonical = await reconstructCanonicalWithMetadata({
                supabase,
                solutionId: suggestion.solution_id,
                name: solutionData.name,
                shortDescription: solutionData.short_description,
                imageUrl: solutionData.image_url,
                getCurrentTags,
                details: detailsData.details
              });
              
              setOriginalSolutionDetails(originalCanonical);
              
              // Format the original data with proper indentation for diff view
              const formattedOriginal = JSON.stringify(originalCanonical, null, 2);
              setOriginalJsonData(formattedOriginal);
            } else {
              setOriginalJsonData('{}');
              setOriginalSolutionDetails({});
              setErrorMessage('No original solution details found');
            }
            
            if (solutionData) {
              // Store the full solution data for the preview
              const details = solutionData.details as SolutionDetails || {};
              const tags = await getCurrentTags(supabase, suggestion.solution_id);
              const fullSolution: SolutionData = {
                id: solutionData.id,
                name: solutionData.name,
                category: '',
                shortDescription: solutionData.short_description,
                tags: tags,
                createdBy: details.createdBy || 'Unknown',
                createdAt: solutionData.created_at,
                image: solutionData.image_url,
                details: details,
                officialWebsite: details.officialWebsite,
                externalLinks: details.externalLinks,
              };
              setFullSolutionData(fullSolution);
            }
            
            setIsLoading(false);
          } catch (err) {
            console.error('Error fetching original solution data:', err);
            setOriginalJsonData('{}');
            setOriginalSolutionDetails({});
            setIsLoading(false);
          }
        };
        
        fetchOriginalData();
      } else {
        setOriginalJsonData('{}');
        setOriginalSolutionDetails(null);
        setIsLoading(false);
      }
    }
  }, [open, suggestion, isNewSolution]);
  
  const handleAcceptClick = async () => {
    let finalJsonData = undefined;
    
    if (isEditing) {
      try {
        // First convert to canonical format
        const editedSuggestion = JSON.parse(editedJsonData);
        const canonicalSuggestion = convertToCanonicalFormat(editedSuggestion);
        
        // Then convert to solutions table format (without metadata)
        // TODO: Look into converting directly to solutions table format in the future
        const solutionsTableFormat = convertToSolutionsTableFormat(canonicalSuggestion);
        finalJsonData = JSON.stringify(solutionsTableFormat);
      } catch (error) {
        console.error('Error parsing edited JSON:', error);
        // Fall back to raw edited data if parsing fails
        finalJsonData = editedJsonData;
      }
    }
    
    // Extract tags from the suggestion to validate
    let tagsToValidate: string[] = [];
    try {
      const suggestionContent = suggestion.suggestion ? JSON.parse(suggestion.suggestion) : {};
      tagsToValidate = suggestionContent.metadata?.tags || [];
    } catch (error) {
      console.error('Error parsing suggestion for tag validation:', error);
    }
    
    // Validate tags before acceptance
    if (tagsToValidate.length > 0) {
      try {
        const validationResult = await validateTags(tagsToValidate);
        
        if (validationResult.hasNewTags) {
          // Store acceptance data and show validation dialog
          setPendingAcceptanceData({
            reviewerNotes,
            finalJsonData,
            validationResult
          });
          setShowTagValidation(true);
          return;
        }
      } catch (error) {
        console.error('Tag validation error:', error);
        // If validation fails, proceed with acceptance anyway
      }
    }
    
    // No new tags or validation failed, proceed with normal acceptance
    onAccept(reviewerNotes, finalJsonData);
  };
  
  const handleTagValidationConfirm = () => {
    if (pendingAcceptanceData) {
      setShowTagValidation(false);
      onAccept(pendingAcceptanceData.reviewerNotes, pendingAcceptanceData.finalJsonData);
      setPendingAcceptanceData(null);
    }
  };
  
  const handleTagValidationCancel = () => {
    setShowTagValidation(false);
    setPendingAcceptanceData(null);
  };
  
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  // Render a preview of solution details
  const renderSolutionPreview = (details: SolutionDetails | null) => {
    if (!details) return <p>No details available</p>;
    
    return (
      <div className="space-y-6 p-4">
        {/* Overview Section */}
        {details.overview && (
          <div>
            <h3 className="text-lg font-medium mb-2 text-foreground">Overview</h3>
            <p className="text-muted-foreground">{details.overview}</p>
          </div>
        )}
        
        {/* Capabilities Section */}
        {details.capabilities && details.capabilities.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-2 text-foreground">Key Capabilities</h3>
            <div className="space-y-2">
              {Array.isArray(details.capabilities) && details.capabilities.map((capability: any, index: number) => (
                <div key={index} className="border p-3 rounded-md">
                  {typeof capability === 'string' ? (
                    <p>{capability}</p>
                  ) : (
                    <div>
                      <h4 className="font-medium">{capability.title}</h4>
                      <p className="text-sm text-muted-foreground">{capability.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Use Cases Section */}
        {details.useCases && details.useCases.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-2 text-foreground">Use Cases</h3>
            <div className="space-y-2">
              {Array.isArray(details.useCases) && details.useCases.map((useCase: any, index: number) => (
                <div key={index} className="border p-3 rounded-md">
                  {typeof useCase === 'string' ? (
                    <p>{useCase}</p>
                  ) : (
                    <div>
                      <h4 className="font-medium">{useCase.title}</h4>
                      <p className="text-sm text-muted-foreground">{useCase.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Limitations Section */}
        {details.limitations && details.limitations.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-2 text-foreground">Limitations</h3>
            <div className="bg-yellow-50/30 dark:bg-yellow-900/10 border border-yellow-200/30 p-3 rounded-md">
              <ul className="list-disc pl-5">
                {details.limitations.map((limitation: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{limitation}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* External Links */}
        {details.externalLinks && details.externalLinks.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-2 text-foreground">External Links</h3>
            <div className="space-y-1">
              {details.externalLinks.map((link: any, index: number) => (
                <div key={index} className="text-primary">{link.title}: {link.url}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-lg max-h-[80vh] sm:w-[90%] sm:max-w-4xl sm:max-h-[90vh] overflow-y-auto px-4 pb-0">
        <DialogHeader>
          <DialogTitle>
            Review {isNewSolution ? 'New Solution' : 'Suggested Edit'}
          </DialogTitle>
          <DialogDescription>
            Submitted by {suggestion?.suggester_name || 'Unknown'} ({suggestion?.email || 'No email'}) on{' '}
            {suggestion?.created_at ? format(new Date(suggestion.created_at), 'MMMM d, yyyy HH:mm') : 'unknown date'}
            {isNewSolution && (
              <span className="block mt-1 text-sm text-blue-600">
                This is a new solution submission (not an edit to existing solution)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        {/* Reviewer notes section - always visible regardless of tab */}
        <div>
          <Label htmlFor="reviewer-notes">Reviewer Notes (Optional)</Label>
          <Textarea
            id="reviewer-notes"
            placeholder="Add your notes about this suggestion..."
            className="mt-2"
            value={reviewerNotes}
            onChange={(e) => setReviewerNotes(e.target.value)}
          />
        </div>
        
        <div className="space-y-4 my-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <TabsList>
                {!isNewSolution && <TabsTrigger value="diff">Diff View</TabsTrigger>}
                <TabsTrigger value="suggested">Suggested</TabsTrigger>
              </TabsList>
              
              {/* Preview Buttons */}
              <div className="flex flex-wrap sm:flex-nowrap gap-2">
                {!isNewSolution && (
                  <PreviewButton
                    jsonContent={originalJsonData}
                    solutionName={fullSolutionData?.name}
                    solutionShortDescription={fullSolutionData?.shortDescription}
                    solutionTags={fullSolutionData?.tags}
                    imageUrl={fullSolutionData?.image}
                    className="text-sm"
                    text="Original (Preview)"
                  />
                )}
                <PreviewButton
                  jsonContent={newJsonData}
                  solutionName={suggestedSolutionDetails?.metadata?.name}
                  solutionShortDescription={suggestedSolutionDetails?.metadata?.shortDescription}
                  solutionTags={suggestedSolutionDetails?.metadata?.tags}
                  imageUrl={suggestedSolutionDetails?.metadata?.imageUrl}
                  className="text-sm"
                  text={isNewSolution ? "New Solution (Preview)" : "Suggested (Preview)"}
                />
              </div>
            </div>
          
            {/* Suggested Tab Content - Reusable for both edits and new solutions */}
            <TabsContent value="suggested" className="mt-2">
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <Label htmlFor="edit-json">{isNewSolution ? 'New Solution' : 'Edit Suggestion'}</Label>
                    <p className="text-xs text-muted-foreground">
                      JSON has been normalized to canonical format for consistency
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm" 
                    onClick={toggleEditMode}
                    className="flex items-center gap-1"
                  >
                    <Edit className="h-4 w-4" />
                    {isEditing ? "Cancel Edit" : "Edit"}
                  </Button>
                </div>
                {isEditing ? (
                  <Textarea
                    id="edit-json"
                    value={editedJsonData}
                    onChange={(e) => setEditedJsonData(e.target.value)}
                    className="font-mono text-sm h-80 mt-2"
                  />
                ) : (
                  <div className="border rounded-md overflow-auto max-h-[500px] mt-2">
                    <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words">
                      {newJsonData || '{}'}
                    </pre>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Diff Tab Content - Only for edit suggestions */}
            {!isNewSolution && (
              <TabsContent value="diff" className="mt-2">
                {isLoading ? (
                  <div className="p-4 text-center">Loading original data...</div>
                ) : errorMessage ? (
                  <div className="p-4 text-center text-red-500 border border-red-200 rounded-md">
                    {errorMessage}
                  </div>
                ) : (
                  <div className="border rounded-md overflow-auto max-h-[500px]">
                    <h3 className="font-medium p-2 bg-slate-100 border-b">Diff Comparison</h3>
                    <div className="overflow-x-auto">
                      <ReactDiffViewer
                        oldValue={originalJsonData || '{}'}
                        newValue={newJsonData || '{}'}
                        splitView={true}
                        leftTitle="Original Solution Details"
                        rightTitle="Suggested Changes"
                        useDarkTheme={false}
                        hideLineNumbers={false}
                        showDiffOnly={false}
                        renderContent={(str) => {
                          return str ? <pre className="whitespace-pre-wrap break-words">{str}</pre> : null;
                        }}
                        styles={{
                          contentText: {
                            fontSize: '0.875rem',
                            fontFamily: 'monospace',
                            lineHeight: '1.5',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                          },
                          diffContainer: {
                            display: 'flex',
                            width: '100%',
                            minWidth: '0',
                          },
                          splitView: {
                            display: 'flex',
                            width: '100%',
                          },
                          diffRemoved: {
                            backgroundColor: '#ffebe9',
                          },
                          diffAdded: {
                            backgroundColor: '#e6ffec',
                          },
                          line: {
                            wordBreak: 'break-word',
                          },
                          marker: {
                            minWidth: '30px',
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
        
        <DialogFooter className="sticky bottom-0 bg-background py-4 border-t">
          <div className="flex justify-start sm:justify-end px-4 sm:px-6 py-2 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => onReject(reviewerNotes)}
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
              <Button
                onClick={handleAcceptClick}
                disabled={isValidating}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {isValidating ? 'Processing...' : (isNewSolution ? 'Accept New Solution' : `Accept ${isEditing ? 'with Edits' : ''}`)}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* Tag Validation Dialog */}
      {pendingAcceptanceData && (
        <TagValidationDialog
          open={showTagValidation}
          onOpenChange={setShowTagValidation}
          newTags={pendingAcceptanceData.validationResult?.newTags || []}
          existingTags={pendingAcceptanceData.validationResult?.existingTags || []}
          onConfirm={handleTagValidationConfirm}
          onCancel={handleTagValidationCancel}
          context="review"
          isLoading={false}
        />
      )}
    </Dialog>
  );
};

export default ReviewSuggestionDialog;
